import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { fillScreenAnimation } from '../shared/animations/animations';
import { IDisplayInfo } from '@caslink/device-client/ng-app/shared/models/models';
import { FormGroup } from '@angular/forms';
import { DeviceInformation } from '@caslink/device-client/ng-app/shared/models/device-information';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { CommandService } from '@caslink/device-client/ng-app/shared/services/command.service';

@Component({
    selector: 'dl-card-nest-slider-wrapper',
    templateUrl: './card-nest-slider-wrapper.component.html',
    styleUrls: ['./card-nest-slider-wrapper.component.scss', '../card-display-info/common-list-items.scss'],
    animations: [fillScreenAnimation],
})
export class CardNestSliderWrapperComponent {

    @Input() heating = false;
    @Input() cooling = false;
    @Input() displayInfo: IDisplayInfo;
    @Input() displayInfoForEdit: IDisplayInfo;
    @Input() isEditMode: boolean;
    @Input() deviceInputForm: FormGroup;
    @Input() deviceInfo: DeviceInformation;
    @Input() setDeviceInfo: (deviceInfo: DeviceInformation) => void;

    nestSliderSize = 165;

    get currentDisplayInfo() {
        return this.isEditMode ? this.displayInfoForEdit : this.displayInfo;
    }

    get temperature() {
        if (this.displayInfo?.temperature !== undefined) {
            return (this.displayInfo?.temperature.text + (this.displayInfo?.temperature.units ? this.displayInfo?.temperature.units : ''));
        } else {
            return '';
        }
    }

    get relativeHumidity() {
        if (this.displayInfo?.relativeHumidity !== undefined) {
            return (this.displayInfo?.relativeHumidity.text + (this.displayInfo?.relativeHumidity.units ? this.displayInfo?.relativeHumidity.units : ''));
        } else {
            return '';
        }
    }

    get occupied() {
        return this.currentDisplayInfo?.occupancy?.occupiedOn;
    }

    set occupied(val: boolean) {
        this.currentDisplayInfo.occupancy.occupiedOn = val;
    }

    get showSlider() {
        return (
            this.occupied && (this.currentDisplayInfo?.occupiedCoolingSetpoint !== undefined || this.currentDisplayInfo?.occupiedHeatingSetpoint !== undefined) ||
            !this.occupied && (this.currentDisplayInfo?.unoccupiedCoolingSetpoint !== undefined || this.currentDisplayInfo?.unoccupiedHeatingSetpoint !== undefined)
        );
    }

    get nestSliderOriginX() {
        return this.nestSliderHalfSize;
    }

    get nestSliderOriginY() {
        return this.nestSliderHalfSize;
    }

    get setpointMinimum() {
        return this.occupied ? this.currentDisplayInfo?.occupiedSetpointMinimum : this.currentDisplayInfo?.unoccupiedSetpointMinimum;
    }

    get setpointMaximum() {
        return this.occupied ? this.currentDisplayInfo?.occupiedSetpointMaximum : this.currentDisplayInfo?.unoccupiedSetpointMaximum;
    }

    get coolingSetpoint() {
        return Math.round(this.occupied ? this.currentDisplayInfo?.occupiedCoolingSetpoint : this.currentDisplayInfo?.unoccupiedCoolingSetpoint);
    }

    set coolingSetpoint(value: number) {
        if (value === undefined || value === null) {
            return;
        }
        const prop = this.occupied ? 'occupiedCoolingSetpoint' : 'unoccupiedCoolingSetpoint';
        this.storeSetpointValues(prop, value);
    }

    get heatingSetpoint() {
        return Math.round(this.occupied ? this.currentDisplayInfo?.occupiedHeatingSetpoint : this.currentDisplayInfo?.unoccupiedHeatingSetpoint);
    }

    set heatingSetpoint(value: number) {
        if (value === undefined || value === null) {
            return;
        }
        const prop = this.occupied ? 'occupiedHeatingSetpoint' : 'unoccupiedHeatingSetpoint';
        this.storeSetpointValues(prop, value);
    }

    private nestSliderHalfSize = this.nestSliderSize / 2;

    constructor(private changeDetectorRef: ChangeDetectorRef,
                private commandService: CommandService,
    ) {
    }

    startEditing = () => {
        if (!this.displayInfoForEdit.dirty) {
            this.displayInfoForEdit.dirty = true;
            this.updateFormChange();

            // Ensure child components that call startEditing are notified of these changes immediately.
            this.changeDetectorRef.detectChanges();
        }
    };

    updateFormChange() {
        this.deviceInputForm?.markAsDirty();
        this.deviceInputForm?.markAllAsTouched();
        this.deviceInputForm?.updateValueAndValidity();
    }

    onOccupiedChange($event: MatButtonToggleChange) {
        this.updateFormChange();
        this.propagateOccupancyChangeToDeviceHierarchyModel($event?.value);
    }

    onCoolingSetpointDebouncedChange(value: number) {
        const prop = this.occupied ? 'occupiedCoolingSetpoint' : 'unoccupiedCoolingSetpoint';
        this.propagateSensorChangeToDeviceHierarchyModel(`${prop}SensorId`, value);
    }

    onHeatingSetpointDebouncedChange(value: number) {
        const prop = this.occupied ? 'occupiedHeatingSetpoint' : 'unoccupiedHeatingSetpoint';
        this.propagateSensorChangeToDeviceHierarchyModel(`${prop}SensorId`, value);
    }

    private storeSetpointValues(prop: string, value: number) {
        if (!CardNestSliderWrapperComponent.isInAllowedRange(value, this.setpointMinimum, this.setpointMaximum)) {
            return;
        }
        if (this.displayInfo[prop] && Math.round(this.displayInfo[prop]) !== value) {
            this.startEditing();
        }

        this.currentDisplayInfo[prop] = Math.round(value);
        if (!this.currentDisplayInfo[prop] && value) {
            this.currentDisplayInfo[prop] = Math.round(value);
        }
    }

    private static isInAllowedRange(value: number, min: number, max: number) {
        return value && min <= value && value <= max;
    }

    private propagateSensorChangeToDeviceHierarchyModel(prop: string, value: number) {
        const sensors = this.deviceInfo?.$deviceHierarchy?.$flatControlInputs;
        if (sensors) {
            const sensorId = this.displayInfoForEdit[prop];
            const sensor = sensors.find(s => s.sensorId === sensorId);
            if (sensor?.changeCommand) {
                sensor.value = CardNestSliderWrapperComponent.limitSensorValue(sensor.minValue, sensor.maxValue, value);
                this.commandService.invokeCommand(this.deviceInfo, sensor.changeCommand?.action);
            }
        }
    }

    private propagateOccupancyChangeToDeviceHierarchyModel(occupancy: boolean) {
        const sensorId = this.displayInfoForEdit?.occupancy?.occupiedSensorId;
        const sensor = this.deviceInfo?.$deviceHierarchy?.$flatControlInputs.find(s => s.sensorId === sensorId);
        if (sensor?.changeCommand) {
            sensor.value = occupancy;
            this.commandService.invokeCommand(this.deviceInfo, sensor.changeCommand?.action);
        }
    }

    private static limitSensorValue(minValue: number, maxValue: number, value: number) {
        if (minValue !== undefined && maxValue !== undefined) {
            if (value < minValue) {
                return minValue;
            }
            if (value > maxValue) {
                return maxValue;
            }
        }
        return value;
    }
}
